import { useEffect, useState, useCallback, useRef } from "react";
import { useParams } from "react-router-dom";
import {
  getLoanRequest,
  getDocumentTypesListRequest,
  getDocumentsListRequest, // ✅ new import
} from "@/services";

export function useDocumentCollectionData() {
  const { applicationId: applicationUuid } = useParams();
  const [loanDetails, setLoanDetails] = useState({});
  const [documentsList, setDocumentsList] = useState([]); // ✅ new state
  const [documentTypesList, setDocumentTypesList] = useState([]); // ✅ new state
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [loadError, setLoadError] = useState(null);

  // --- Loan Details ---
  const fetchLoanDetails = useCallback(async () => {
    try {
      const loanData = await getLoanRequest({ loan_id: applicationUuid });
      setLoanDetails((loanData?.results || [])[0]);
    } catch (err) {
      console.error("Error fetching Business KYC:", err);
      setError(err);
    }
  }, []);

  const reloadLoanDetails = useCallback(fetchLoanDetails, [fetchLoanDetails]);

  // --- Documents ---
  const fetchDocumentsList = useCallback(async () => {
    try {
      const docsRes = await getDocumentsListRequest({ loan_id: applicationUuid });
      setDocumentsList(docsRes?.results || docsRes || []);
    } catch (err) {
      console.error("Error fetching Documents:", err);
      setError(err);
    }
  }, []);

  const reloadDocumentsList = useCallback(fetchDocumentsList, [fetchDocumentsList]);

  // --- Documents ---
  const fetchDocumentTypesList = useCallback(async () => {
    try {
      const docTypesRes = await getDocumentTypesListRequest();
      setDocumentsList(docTypesRes?.results || docTypesRes || []);
    } catch (err) {
      console.error("Error fetching Documents:", err);
      setError(err);
    }
  }, []);

  const reloadDocumentTypesList = useCallback(fetchDocumentTypesList, [fetchDocumentTypesList]);


  // --- Initial load ---
  const isMountedRef = useRef(true);
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const loadAll = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const responses = await Promise.all([
        getLoanRequest({ loan_id: applicationUuid }),
        getDocumentsListRequest({ loan_id: applicationUuid }),
        getDocumentTypesListRequest({ category: 'data_collection' })
      ]);

      if (!isMountedRef.current) return;

      // apiRequest resolves with { success: false } instead of throwing
      const failed = responses.find((res) => res?.success === false);
      if (failed) {
        console.error("Error fetching documents data:", failed.error);
        setLoadError(failed.error || true);
        return;
      }

      const [loanRes, docsRes, docTypeRes] = responses;
      setLoanDetails((loanRes?.results || [])[0]);
      setDocumentsList(docsRes?.results || docsRes || []);
      setDocumentTypesList(docTypeRes?.results || docTypeRes || []);
    } catch (err) {
      if (isMountedRef.current) {
        console.error("Error fetching documents data:", err);
        setLoadError(err || true);
      }
    } finally {
      if (isMountedRef.current) setLoading(false);
    }
  }, [applicationUuid]);

  useEffect(() => {
    loadAll();
  }, [loadAll]);

  return {
    loanDetails,
    documentsList,
    documentTypesList,
    loading,
    error,
    loadError,
    retryLoad: loadAll,
    reloadLoanDetails,
    reloadDocumentsList, // ✅ new method
    reloadDocumentTypesList,
  };
}
